import { sendReward, useConductricsSelection, Status } from '@dx-ui/framework-conductrics';
import { SPEECH_BUBBLE_ITERATION_AGENT_ID } from './constants/conductrics';
import set from 'lodash/set';
import { getLuminance } from 'polished';
import type { Surfaces } from '@dx-ui/global-ui-prism';

import { GuestQuery } from '@dx-ui/framework-auth-provider';
import { useState, useEffect, createContext } from 'react';

import { env } from '@dx-ui/framework-env';
import type { GlobalNavigationClick, HeaderClickIDs, UserLinks, HeaderLogoVariant } from './types';
import { useRouter } from 'next/router';
import { useQueryLanguage } from '@dx-ui/framework-i18n';

const navigationInteractionGoal = 'g-6gRqwMh3Pt';
const globalNavMenuOpenGoal = 'g-hTnnzBAh3J';
const globalNavMenuLinkClickGoal = 'g-TMeHOg0W2z';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

export const SpeechBubbleContext = createContext<{
  isShowSpeechBubble: boolean;
  isVariantB6266MVT?: boolean;
  isBookSpeechBubble?: boolean;
  onDialogOpen?: () => void;
  onDismiss?: () => void;
  shouldShowOnMobile?: boolean;
}>({
  isShowSpeechBubble: false,
  isVariantB6266MVT: false,
  isBookSpeechBubble: false,
  shouldShowOnMobile: false,
});

export const sendInteractionReward = () => {
  sendReward(navigationInteractionGoal);
};

export const sendGlobalNavMenuOpenGoal = () => {
  sendReward(globalNavMenuOpenGoal);
};

export const sendGlobalNavLinkClickGoal = () => {
  sendReward(globalNavMenuLinkClickGoal);
};

export function removeSpacesAndAmpersands(str: string) {
  return str.replace(/\s+|&/g, '').toLowerCase();
}

function buildNavClickId(label: string, dropdownLabel?: string) {
  return removeSpacesAndAmpersands(`gh_${dropdownLabel ? `${dropdownLabel}_` : ''}${label}`);
}

export function trackNavClickBasedOnLabel(label: string, dropdownLabel?: string) {
  return trackNavClick(buildNavClickId(label, dropdownLabel));
}

type GlobalNavigationClickHandler = (payload: GlobalNavigationClick) => void;

export function dispatchGlobalNavigationClick(
  clickId: HeaderClickIDs | string,
  onGlobalNavigationClick?: GlobalNavigationClickHandler,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true
): string {
  if (isLegacyGlobalNavigationClickAnalyticsEnabled) {
    return trackNavClick(clickId);
  }

  onGlobalNavigationClick?.({ clickId });
  return clickId;
}

export function dispatchGlobalNavigationClickBasedOnLabel(
  label: string,
  dropdownLabel?: string,
  onGlobalNavigationClick?: GlobalNavigationClickHandler,
  isLegacyGlobalNavigationClickAnalyticsEnabled = true
): string {
  return dispatchGlobalNavigationClick(
    buildNavClickId(label, dropdownLabel),
    onGlobalNavigationClick,
    isLegacyGlobalNavigationClickAnalyticsEnabled
  );
}

export function trackNavClick(value: string) {
  if (window?.digitalData && window?._satellite && value) {
    set(window.digitalData, 'click.clickID', value);
    window._satellite?.track?.('global_click');
  }

  return value;
}

const replaceLang = (stringValue: string, lang: string) => stringValue?.replace('__LANG__', lang);

const DEFAULT_OCODE = 'JHTNW';

const localeSignUpLinkMap: Record<string, string> = {
  'en-IN': `https://portal.asiapac.hilton.com/en-IN/join/?ocode=${DEFAULT_OCODE}`,
};

export const useSpeechBubbleIterationMVT = (isSpeechBubble: boolean) => {
  const { isLoaded, selection } = useConductricsSelection(
    SPEECH_BUBBLE_ITERATION_AGENT_ID,
    isSpeechBubble ? Status.OK : Status.PENDING
  );

  return {
    isVariantB: isLoaded ? selection?.choice === 'B' : undefined,
  };
};

/** Returns the full set of potential nav links, including optional links like SMB and Go Hilton */
export function useBaseNavLinksFromEnv({
  isAuthenticated,
  guestInfo,
}: {
  isAuthenticated: boolean;
  guestInfo: GuestQuery['guest'] | null;
}) {
  const { locale: lang = 'en' } = useRouter();
  const originalLocale = useQueryLanguage();
  const signInUrl = replaceLang(
    env('DX_AUTH_UI', 'https://www.hilton.com/__LANG__/auth2/guest/login/'),
    lang
  );

  const signUpUrl =
    localeSignUpLinkMap[originalLocale] ||
    replaceLang(`${env('OHW_BASE_URL')}__LANG__/hilton-honors/join/?ocode=${DEFAULT_OCODE}`, lang);

  const environmentBaseUrl = env('OHW_BASE_URL', 'https://www.hilton.com/');

  // Core+ returns the path with a leading slash, so we need to remove it
  // TODO: Remove UI-side logic once Core+ is updated to not return the leading slash
  let b2bEntryPath = guestInfo?.hhonors?.programAccountsDetail?.programAccountEntryUrl?.url;
  if (b2bEntryPath?.startsWith('/')) {
    b2bEntryPath = b2bEntryPath.replace('/', '');
  }

  return {
    signInLink: { url: signInUrl },
    signUpLink: { url: signUpUrl },
    accountLink: { url: `${environmentBaseUrl}${lang}/hilton-honors/guest/my-account/` },
    honorsActivityLink: { url: `${environmentBaseUrl}${lang}/hilton-honors/guest/activity/` },
    b2bMemberLink: {
      label: guestInfo?.hhonors?.programAccountsDetail?.programAccountEntryUrl?.label,
      url: `${environmentBaseUrl}${b2bEntryPath}`,
    },
    honorsPointsLink: { url: `${environmentBaseUrl}${lang}/hilton-honors/guest/points/` },
    honorsProfileLink: { url: `${environmentBaseUrl}${lang}/hilton-honors/guest/profile/` },
    yourOffersLink: { url: `${environmentBaseUrl}${lang}/hilton-honors/guest/my-offers/` },
    friendsAndFamilyLink: {
      url: env('TMTP_MANAGE_FAMILY_AND_FRIENDS_LINK', 'https://tmtp.hilton.com/tmtp/main.html'),
    },
    hgvMaxLink: {
      url: env(
        'EXTERNAL_HGV_PROGRAM_DETAILS_URL',
        'https://help.hilton.com/s/article/HGV-Max-rate'
      ),
    },
    goHiltonLink: {
      url: env(
        'EXTERNAL_TMTP_PROGRAM_DETAILS_URL',
        'https://help.hilton.com/s/article/What-is-the-Go-Hilton-employee-program'
      ),
    },
    findStayLink: {
      url: isAuthenticated
        ? `${environmentBaseUrl}${lang}/hilton-honors/guest/activity/`
        : `${environmentBaseUrl}${lang}/book/reservation/find/`,
    },
  } as const satisfies UserLinks;
}

export function useWindowScroll(debounceDelay?: number) {
  const [windowScroll, setWindowScroll] = useState<{
    scrollY?: number;
    scrollX?: number;
    scrollDirection?: 'up' | 'down';
  }>({
    scrollY: undefined,
    scrollX: undefined,
    scrollDirection: undefined,
  });

  useEffect(() => {
    let lastScrollY = window?.scrollY;

    function handleScroll() {
      const scrollY = window?.scrollY;
      let scrollDirection: 'up' | 'down' | undefined;

      if (scrollY > lastScrollY) {
        scrollDirection = 'down';
      } else if (scrollY < lastScrollY) {
        scrollDirection = 'up';
      }

      lastScrollY = scrollY;

      setWindowScroll({
        scrollY,
        scrollX: window?.scrollX,
        scrollDirection,
      });
    }

    const throttledScroll = throttle(handleScroll, debounceDelay || 50, {
      leading: true,
      trailing: true,
    });
    window?.addEventListener('scroll', throttledScroll);
    handleScroll();
    return () => {
      throttledScroll.cancel();
      window?.removeEventListener('scroll', throttledScroll);
    };
  }, [debounceDelay]);

  return windowScroll;
}

// Tracks layout shifts and DOM mutations, invokes a callback
export function useLayoutStableCallback(callback: () => void, returnEarly?: boolean) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined' || returnEarly)
      return;

    const layoutShiftObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // @ts-expect-error: hadRecentInput is part of LayoutShift
        if (entry.entryType === 'layout-shift' && !entry.hadRecentInput) {
          callback();
        }
      }
    });

    layoutShiftObserver.observe({ type: 'layout-shift', buffered: true });

    const debouncedCallback = debounce(callback, 200);

    const mutationObserver = new MutationObserver(() => {
      debouncedCallback();
    });

    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      layoutShiftObserver.disconnect();
      mutationObserver.disconnect();
      debouncedCallback.cancel();
    };
  }, [callback, returnEarly]);
}

//Tracks if user has interacted with the page
export function useUserInteractedWithPage(returnEarly?: boolean) {
  const [isUserInteracted, setIsUserInteracted] = useState(false);
  useEffect(() => {
    if (returnEarly) return;

    const handlePointerDown = () => {
      setIsUserInteracted(true);
    };

    const handleKeyPress = () => {
      setIsUserInteracted(true);
    };

    const handleWheel = () => {
      setIsUserInteracted(true);
    };

    const handleTouchStart = () => {
      setIsUserInteracted(true);
    };

    const handleMouseDown = () => {
      setIsUserInteracted(true);
    };

    document.addEventListener('pointerdown', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyPress, { passive: true });
    document.addEventListener('wheel', handleWheel, { passive: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('mousedown', handleMouseDown, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [returnEarly]);

  return isUserInteracted;
}

export const mapBrandCodeToSubmenuSurface = (
  brandCode: string,
  isReimagineThemeExperience?: boolean
) => {
  if (brandCode === 'ID') return 'alternate';
  if (brandCode === 'OL') return 'contrast';
  if (brandCode === 'WW' && isReimagineThemeExperience) return 'contrast';
};

/** Prism surfaces that are contractually dark (light text/logo needed on top of them). */
export const darkSurfaces: Surfaces[] = ['inverse', 'contrast'];

/**
 * Decides the HeaderLogo image variant from DS theme colors using perceived brightness,
 * falling back to a static surface type when colors are unavailable.
 *
 * Luminance is a 0–1 WCAG measure of brightness. When the surface text is brighter
 * than its background (`luminance(text) > luminance(background)`) the background is
 * dark, so the inverted (white) logo variant is needed.
 *
 * Reads hex values only (no DOM) — SSR-safe and flash-free on hydration. When either
 * token is missing or invalid it derives darkness from `fallbackSurface` instead.
 */
export function getLogoVariantFromThemeColors({
  text,
  background,
  fallbackSurface = 'base',
}: {
  text?: string;
  background?: string;
  fallbackSurface?: Surfaces;
}): HeaderLogoVariant {
  const isFallbackDark = darkSurfaces.includes(fallbackSurface);

  const isDarkSurface = (() => {
    if (!text || !background) return isFallbackDark;
    try {
      return getLuminance(text) > getLuminance(background);
    } catch {
      return isFallbackDark;
    }
  })();

  if (isDarkSurface) return 'inverted';
  return 'default';
}
